import { Link } from 'react-router-dom';
import SharePanel from '@/components/feature/SharePanel';

export default function CTASection() {
  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-br from-pink-900 via-green-900 to-pink-800">
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-green-500/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-400/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Floating petals */}
      {[
        { top: '15%', left: '8%', size: 10 },
        { top: '70%', left: '5%', size: 7 },
        { top: '30%', right: '6%', size: 9 },
        { top: '75%', right: '10%', size: 6 },
        { top: '50%', left: '15%', size: 5 },
        { top: '20%', right: '18%', size: 8 },
      ].map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: p.top,
            left: (p as { left?: string }).left,
            right: (p as { right?: string }).right,
            width: p.size,
            height: p.size,
            backgroundColor: i % 2 === 0 ? 'rgba(244,114,182,0.45)' : 'rgba(134,239,172,0.35)',
            filter: 'blur(1px)',
            animation: `floatPetal ${2.5 + i * 0.4}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        ></div>
      ))}

      <style>{`
        @keyframes floatPetal {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(-14px) rotate(12deg); }
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-4 py-1.5 mb-6">
          <i className="ri-seedling-line text-amber-400 text-sm"></i>
          <span className="text-amber-300 text-sm font-medium">一位父亲的心愿 · 千万孩子的未来</span>
        </div>

        <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
          开启你的
          <span className="text-amber-400">金榜之路</span>
        </h2>
        <p className="text-white/75 text-base md:text-lg mb-10 max-w-lg mx-auto leading-relaxed">
          无论你在城市还是乡村，无论你备考高考还是考研，金榜旺财都是你最好的学习伙伴。城乡孩子取长补短，共同进步，共同金榜题名！
        </p>

        <div className="flex flex-wrap justify-center gap-4 mb-14">
          <Link
            to="/videos"
            className="px-8 py-3.5 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-play-circle-line"></i>
            免费试看课程
          </Link>
          <Link
            to="/community"
            className="px-8 py-3.5 bg-white/15 hover:bg-white/25 border border-white/40 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-team-line"></i>
            加入学习社区
          </Link>
          <SharePanel
            title="金榜旺财 - 城乡学生互助升学平台"
            description="城乡学生互助，AI智能匹配学伴，高考考研视频课程，助力每位学生金榜题名！"
            trigger="button"
          />
        </div>

        {/* Student Avatars Row */}
        <div className="flex flex-wrap justify-center items-center gap-3">
          <div className="flex items-center">
            {[
              'https://public.readdy.ai/ai/img_res/7fb4c6d5d3730abee197b7a4ca19826e.jpg',
              'https://readdy.ai/api/search-image?query=young%20Chinese%20girl%20student%20portrait%20smiling%2C%20clean%20white%20background%2C%20primary%20school&width=60&height=60&seq=ca002&orientation=squarish',
              'https://readdy.ai/api/search-image?query=Chinese%20teenage%20boy%20student%20portrait%2C%20clean%20white%20background%2C%20middle%20school&width=60&height=60&seq=ca003&orientation=squarish',
              'https://readdy.ai/api/search-image?query=Chinese%20teenage%20girl%20student%20portrait%20smiling%2C%20clean%20white%20background%2C%20high%20school&width=60&height=60&seq=ca004&orientation=squarish',
              'https://readdy.ai/api/search-image?query=Chinese%20university%20student%20portrait%20smiling%2C%20clean%20white%20background%2C%20college%20age&width=60&height=60&seq=ca005&orientation=squarish',
            ].map((src, i) => (
              <img
                key={i}
                src={src}
                alt="学员"
                className="w-11 h-11 rounded-full object-cover object-top border-2 border-white/40"
                style={{ marginLeft: i > 0 ? '-10px' : '0' }}
              />
            ))}
          </div>
          <span className="text-white/70 text-sm ml-2">已有 <strong className="text-amber-300">50万+</strong> 学生加入</span>
        </div>

        {/* Mini stats row */}
        <div className="mt-10 flex flex-wrap justify-center gap-8">
          {[
            { num: '2万+', label: '教学视频' },
            { num: '8000+', label: '优秀教师' },
            { num: '98%', label: '升学率' },
            { num: '31省', label: '覆盖全国' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-xl font-black text-amber-400">{s.num}</div>
              <div className="text-white/50 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* QR Code / Install App Section */}
        <div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          {/* WeChat QR */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-36 h-36 bg-white rounded-2xl flex items-center justify-center p-2 relative overflow-hidden">
              {/* Placeholder QR pattern */}
              <div className="w-full h-full relative">
                <img
                  src="https://readdy.ai/api/search-image?query=QR%20code%20black%20and%20white%20square%20pattern%20clean%20minimal%20on%20pure%20white%20background%2C%20scannable%20barcode%20matrix&width=128&height=128&seq=qr001&orientation=squarish"
                  alt="微信扫码访问金榜旺财"
                  className="w-full h-full object-cover rounded-lg"
                />
                {/* Center logo overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white">
                    <img
                      src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                      alt="金榜旺财"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-sm flex items-center gap-1.5 justify-center">
                <i className="ri-wechat-line text-green-400 text-base"></i>
                微信扫码访问
              </p>
              <p className="text-white/50 text-xs mt-0.5">微信内直接打开网站</p>
              <p className="text-amber-400/70 text-xs mt-1 italic">（请替换为真实二维码）</p>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:flex flex-col items-center gap-2">
            <div className="w-px h-16 bg-white/20"></div>
            <span className="text-white/30 text-xs">或</span>
            <div className="w-px h-16 bg-white/20"></div>
          </div>
          <div className="md:hidden w-16 h-px bg-white/20"></div>

          {/* Install App card */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-36 h-36 bg-white/10 border border-white/20 rounded-2xl flex flex-col items-center justify-center gap-2">
              <div className="w-14 h-14 rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                  alt="金榜旺财App"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center px-2">
                <p className="text-white font-bold text-xs">金榜旺财</p>
                <p className="text-white/50 text-xs">Web App</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-sm flex items-center gap-1.5 justify-center">
                <i className="ri-smartphone-line text-amber-400 text-base"></i>
                添加到桌面
              </p>
              <p className="text-white/50 text-xs mt-0.5">像App一样使用，无需下载</p>
              <p className="text-white/40 text-xs mt-1">支持 iOS · Android · 华为 · 小米</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
